// Окружения уровней: небо-градиент, земля, свет, пропсы, погода.
// Каждая тема — маленький мир из примитивов. applyTheme() полностью
// пересобирает окружение и возвращает { update, dispose }.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

// --- Помощники ---

const SKY_MAX_STOPS = 4;

// Купол неба считается в шейдере: канвас 128px давал ступеньки на плавных
// закатах, а дизеринг по пикселю добивает бандинг на дешёвых экранах.
// Стоп 0 — зенит, стоп 1 — линия горизонта (как было у канвас-градиента).
const SKY_VERTEX = /* glsl */`
  varying float vH;
  void main() {
    vH = normalize(position).y * 0.5 + 0.5;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const SKY_FRAGMENT = /* glsl */`
  uniform vec3 uColors[${SKY_MAX_STOPS}];
  uniform float uStops[${SKY_MAX_STOPS}];
  uniform int uCount;
  varying float vH;

  void main() {
    float t = clamp(1.0 - vH, 0.0, 1.0);
    vec3 col = uColors[0];
    for (int i = 0; i < ${SKY_MAX_STOPS} - 1; i++) {
      if (i + 1 >= uCount) break;
      float k = smoothstep(uStops[i], uStops[i + 1], t);
      col = mix(col, uColors[i + 1], k);
    }
    // Дизеринг ±1/255: разбивает полосы на плавных участках
    float n = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
    col += (n - 0.5) / 255.0;
    gl_FragColor = vec4(col, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function gradientSky(stops) {
  const colors = [];
  const positions = [];
  for (let i = 0; i < SKY_MAX_STOPS; i++) {
    const s = stops[Math.min(i, stops.length - 1)];
    colors.push(new THREE.Color(s[1]));   // из sRGB в рабочее линейное
    positions.push(s[0]);
  }
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uColors: { value: colors },
      uStops: { value: positions },
      uCount: { value: Math.min(stops.length, SKY_MAX_STOPS) },
    },
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(150, 24, 16), material);
}

function lambert(color, emissive) {
  const m = new THREE.MeshLambertMaterial({ color });
  if (emissive) {
    m.emissive = new THREE.Color(emissive);
    m.emissiveIntensity = 1;
  }
  return m;
}

function box(w, h, d, mat, x, y, z) {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  m.position.set(x, y, z);
  return m;
}

function tree(x, z, scale = 1) {
  const g = new THREE.Group();
  const trunk = box(0.3 * scale, 1.6 * scale, 0.3 * scale, lambert(0x7a5230), 0, 0.8 * scale, 0);
  const crown = new THREE.Mesh(new THREE.IcosahedronGeometry(1.1 * scale, 0), lambert(0x4e8f3a));
  crown.position.y = 2.2 * scale;
  crown.castShadow = true;
  g.add(trunk, crown);
  g.position.set(x, 0, z);
  return g;
}

function palm(x, z) {
  const g = new THREE.Group();
  const trunk = box(0.25, 3, 0.25, lambert(0x9c7a4f), 0, 1.5, 0);
  trunk.rotation.z = 0.12;
  g.add(trunk);
  for (let i = 0; i < 5; i++) {
    const leaf = box(1.8, 0.08, 0.4, lambert(0x2f9e4f), 0, 0, 0);
    leaf.position.set(Math.cos(i * 1.26) * 0.9 + 0.35, 3.1, Math.sin(i * 1.26) * 0.9);
    leaf.rotation.z = -0.35 * Math.cos(i * 1.26);
    leaf.rotation.y = i * 1.26;
    g.add(leaf);
  }
  g.position.set(x, 0, z);
  return g;
}

// Облако: сглаженные сферы (detail 1) в естественной композиции —
// крупное ядро, бока пониже, шапка сверху. tint — цвет под настроение темы.
function cloudPuff(scale = 1, tint = 0xffffff) {
  // Сферы внутри облака неподвижны друг относительно друга — склеиваем
  // в один меш: дрейфует облако целиком, а draw call один вместо 4-6
  const parts = [];
  const core = new THREE.IcosahedronGeometry(1.1 * scale, 1);
  core.scale(1, 0.7, 1);
  parts.push(core);
  const n = 2 + Math.floor(Math.random() * 3);
  for (let j = 0; j < n; j++) {
    const side = new THREE.IcosahedronGeometry((0.55 + Math.random() * 0.45) * scale, 1);
    side.scale(1, 0.65, 1);
    side.translate(
      (j % 2 ? 1 : -1) * (0.9 + Math.random() * 0.5) * scale * (1 + Math.floor(j / 2) * 0.6),
      -0.15 * scale + Math.random() * 0.2,
      (Math.random() - 0.5) * 0.5 * scale
    );
    parts.push(side);
  }
  const top = new THREE.IcosahedronGeometry(0.6 * scale, 1);
  top.translate(0.3 * scale, 0.55 * scale, 0);
  parts.push(top);
  const mesh = new THREE.Mesh(mergeGeometries(parts), lambert(tint));
  for (const p of parts) p.dispose();
  const g = new THREE.Group();
  g.add(mesh);
  return g;
}

// Партия облаков для темы: разброс по высоте/глубине, свой дрейф у каждого
function addClouds(g, { count = 5, scale = 1, tint = 0xffffff, yMin = 12, yMax = 20, zMin = -26, zMax = -14, speed = 0.4 }) {
  for (let i = 0; i < count; i++) {
    const c = cloudPuff(scale * (0.8 + Math.random() * 0.5), tint);
    c.position.set(-35 + Math.random() * 70, yMin + Math.random() * (yMax - yMin), zMin + Math.random() * (zMax - zMin));
    c.userData.speed = (Math.random() < 0.5 ? -1 : 1) * speed * (0.6 + Math.random() * 0.8);
    g.add(c);
  }
}

function citySilhouette(mat, z, count, maxH, litColor) {
  // Ряд далёких домов; часть окон светится точками-спрайтами не делаем — дорого,
  // вместо этого узкие эмиссивные полосы.
  //
  // Дома статичны, поэтому весь ряд склеиваем в одну геометрию: три плана
  // по десятку коробок каждый давали под сотню draw calls, а фон того не стоит.
  const g = new THREE.Group();
  const wallGeoms = [];
  const stripGeoms = [];
  let tallest = null; // высокое здание ПОД БИЛБОРД — только в видимой зоне кадра
  for (let i = 0; i < count; i++) {
    const h = 2 + Math.random() * maxH;
    const w = 1.5 + Math.random() * 2.5;
    const x = -40 + i * (85 / count) + Math.random() * 2;
    const wall = new THREE.BoxGeometry(w, h, 2);
    wall.translate(x, h / 2, z);
    wallGeoms.push(wall);
    // Надстройки ломают монотонность крыш: антенны и лифтовые будки
    // мержатся в ту же геометрию — по draw calls бесплатно
    const roofDice = Math.random();
    if (roofDice < 0.3) {
      const antenna = new THREE.BoxGeometry(0.08, 0.9 + Math.random() * 1.4, 0.08);
      antenna.translate(x + (Math.random() - 0.5) * w * 0.5, h + 0.6, z);
      wallGeoms.push(antenna);
    } else if (roofDice < 0.55) {
      const shed = new THREE.BoxGeometry(w * 0.35, 0.5, 0.9);
      shed.translate(x + (Math.random() - 0.5) * w * 0.3, h + 0.25, z);
      wallGeoms.push(shed);
    }
    // Дом под рекламный баннер: в кадре портретной камеры (x ≈ [-8, 7])
    // и повыше, чтобы баннер висел на фасаде на уровне взгляда
    if (x > -8 && x < 7 && h > 4.5 && (!tallest || h > tallest.h)) tallest = { x, h, w, z };
    if (litColor && Math.random() < 0.6) {
      const strip = new THREE.BoxGeometry(w * 0.7, 0.15, 0.05);
      strip.translate(x, h * (0.2 + Math.random() * 0.5), z + 1.05);
      stripGeoms.push(strip);
    }
  }
  g.userData.tallest = tallest;
  g.add(new THREE.Mesh(mergeGeometries(wallGeoms), mat));
  for (const geom of wallGeoms) geom.dispose();
  if (stripGeoms.length) {
    g.add(new THREE.Mesh(mergeGeometries(stripGeoms), lambert(0x000000, litColor)));
    for (const geom of stripGeoms) geom.dispose();
  }
  // Параллакс: дальний план «отстаёт» от подъёма камеры. Перспектива даёт
  // это лишь отчасти — доводим руками, чтобы рост башни читался сильнее.
  g.userData.parallax = 0.14;
  return g;
}

/**
 * Рекламный баннер-растяжка на фасаде фонового дома: «Здесь могла быть
 * ваша реклама». Задел под платные интеграции — потом сюда встанет
 * баннер спонсора.
 */
function adBillboard(width = 4.6) {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 256;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#f5f0e8';
  ctx.fillRect(0, 0, 512, 256);
  ctx.strokeStyle = '#e2231a';
  ctx.lineWidth = 14;
  ctx.strokeRect(10, 10, 492, 236);
  ctx.fillStyle = '#2b2b2b';
  ctx.font = 'bold 44px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('ЗДЕСЬ МОГЛА БЫТЬ', 256, 96);
  ctx.fillText('ВАША РЕКЛАМА', 256, 160);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  // Basic-материал: баннер «подсвечен» и читается даже в ночных темах
  return new THREE.Mesh(
    new THREE.PlaneGeometry(width, width / 2),
    new THREE.MeshBasicMaterial({ map: tex })
  );
}

/**
 * Три плана города на разной глубине. Ближний двигается заметно, дальний
 * почти стоит — это и создаёт ощущение, что башня действительно поднялась.
 * Дальние планы светлее ближних: воздушная перспектива добавляет глубины.
 */
function citySkyline(baseColor, z, count, maxH, litColor, fogColor) {
  const g = new THREE.Group();
  const base = new THREE.Color(baseColor);
  const haze = new THREE.Color(fogColor || 0xffffff);
  for (let i = 0; i < 3; i++) {
    const tint = base.clone().lerp(haze, i * 0.22);
    const layer = citySilhouette(
      lambert(tint.getHex()),
      z - i * 9,
      Math.max(5, count - i * 2),
      maxH * (1 - i * 0.15),
      i === 0 ? litColor : null   // окна светятся только у ближнего плана
    );
    layer.userData.parallax = 0.2 - i * 0.07;
    g.add(layer);
    // Баннер — на фасаде подходящего дома ближнего плана; живёт внутри
    // слоя, поэтому параллакс двигает их вместе
    if (i === 0 && layer.userData.tallest) {
      const t = layer.userData.tallest;
      const bw = Math.min(4.6, t.w * 1.6); // не шире дома с разумным свесом
      const ad = adBillboard(bw);
      ad.position.set(t.x, t.h * 0.62, t.z + 1.06);
      layer.add(ad);
    }
  }
  return g;
}

function stars() {
  const geom = new THREE.BufferGeometry();
  const positions = new Float32Array(220 * 3);
  for (let i = 0; i < 220; i++) {
    // Только верхняя полусфера
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI * 0.45;
    const r = 140;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi) + 5;
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return new THREE.Points(geom, new THREE.PointsMaterial({ color: 0xffffff, size: 0.7, fog: false }));
}

function water(color, z, size = 140) {
  const m = box(size, 0.1, 60, lambert(color), 0, 0.02, z - 30);
  return m;
}

/**
 * Стайка птиц: «галочки» из двух плоскостей, машут крыльями и тянутся
 * клином через кадр. Мир должен жить, пока игрок целится — иначе пауза
 * между блоками ощущается мёртвой.
 */
function birdFlock(count = 5, color = 0x3a3a3a) {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide, fog: false });
  const wingGeom = new THREE.PlaneGeometry(0.55, 0.13);
  for (let i = 0; i < count; i++) {
    const bird = new THREE.Group();
    // Крылья — отдельные меши: они машут независимо, склеить нельзя.
    // Птиц немного (4-6), это единицы draw calls.
    for (const dir of [-1, 1]) {
      const wing = new THREE.Mesh(wingGeom, mat);
      wing.position.x = dir * 0.28;
      bird.add(wing);
    }
    // Клин: каждая следующая птица чуть позади и в стороне
    bird.position.set(-i * 1.1, -Math.abs(i - count / 2) * 0.35, i * 0.4);
    bird.userData.phase = Math.random() * Math.PI * 2;
    g.add(bird);
  }
  g.userData.flock = true;
  g.userData.speed = 2.2 + Math.random();
  g.position.set(-45, 16 + Math.random() * 8, -22);
  return g;
}

function updateFlock(g, dt, elapsed) {
  g.position.x += g.userData.speed * dt;
  if (g.position.x > 48) {
    g.position.x = -48;
    g.position.y = 14 + Math.random() * 10;
  }
  g.position.y += Math.sin(elapsed * 0.7) * dt * 0.6;
  for (const bird of g.children) {
    const flap = Math.sin(elapsed * 9 + bird.userData.phase) * 0.9;
    bird.children[0].rotation.z = flap;
    bird.children[1].rotation.z = -flap;
  }
}

/**
 * Ночной самолёт: сам корпус почти не виден, читается по мигающему
 * красному огню — узнаваемая деталь настоящего ночного неба.
 */
function nightPlane() {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 0.16, 0.16),
    new THREE.MeshBasicMaterial({ color: 0x2a2a33, fog: false })
  );
  g.add(body);
  const beaconMat = new THREE.MeshBasicMaterial({ color: 0xff3b30, fog: false });
  const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.09, 6, 6), beaconMat);
  g.add(beacon);
  g.userData.plane = true;
  g.userData.beacon = beacon;
  g.userData.speed = 3.4;
  g.position.set(-50, 26 + Math.random() * 6, -30);
  return g;
}

function updatePlane(g, dt, elapsed) {
  g.position.x += g.userData.speed * dt;
  if (g.position.x > 52) {
    g.position.x = -52;
    g.position.y = 24 + Math.random() * 8;
  }
  // Проблесковый маячок: короткая вспышка раз в ~1.4 с
  g.userData.beacon.visible = (elapsed % 1.4) < 0.12;
}

const CAR_COLORS = [0xe2231a, 0x3a6ea8, 0xf2f2f2, 0x3d3d3d, 0xd8a12c, 0x4e8f3a];

/**
 * Улица за стройплощадкой: тёмная полоса дороги и машинки в обе стороны.
 * Стройка стоит в живом городе, а не в вакууме — фон перестаёт быть задником.
 */
function street(g, { z = -8, ground = 0x555a5f, night = false } = {}) {
  const road = box(90, 0.06, 2.6, lambert(ground), 0, 0.04, z);
  g.add(road);
  // Разметка: короткие штрихи одной геометрией
  const dashGeoms = [];
  for (let x = -42; x < 44; x += 3.4) {
    const d = new THREE.BoxGeometry(1.3, 0.02, 0.09);
    d.translate(x, 0.08, z);
    dashGeoms.push(d);
  }
  g.add(new THREE.Mesh(mergeGeometries(dashGeoms), lambert(0xd8d8d8)));
  for (const geom of dashGeoms) geom.dispose();

  const count = 3 + Math.floor(Math.random() * 2);
  for (let i = 0; i < count; i++) {
    const dir = Math.random() < 0.5 ? -1 : 1;
    const color = CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)];
    // Машинка: корпус + кабина одной геометрией
    const body = new THREE.BoxGeometry(1.1, 0.32, 0.5);
    body.translate(0, 0.26, 0);
    const cab = new THREE.BoxGeometry(0.55, 0.26, 0.46);
    cab.translate(-0.05, 0.55, 0);
    const merged = mergeGeometries([body, cab]);
    body.dispose(); cab.dispose();
    const car = new THREE.Mesh(merged, lambert(color, night ? 0x1a1408 : null));
    car.position.set(-40 + Math.random() * 80, 0.05, z + dir * 0.62);
    car.userData.car = { speed: dir * (3 + Math.random() * 2.5) };
    car.castShadow = true;
    g.add(car);
  }
}

function updateCars(g, dt) {
  for (const child of g.children) {
    if (!child.userData.car) continue;
    child.position.x += child.userData.car.speed * dt;
    if (child.position.x > 45) child.position.x = -45;
    if (child.position.x < -45) child.position.x = 45;
  }
}

// --- Описания тем ---
// ground: цвет земли; fog: [цвет, near, far]; ambient/sun: свет;
// build(g, ctx): пропсы; update(dt, ctx): погода/анимация.

const THEMES = {
  yard: {
    sky: [[0, '#7ec8f2'], [0.6, '#cfe8f8'], [1, '#eef7fb']],
    ground: 0x86b56a, fog: ['#cfe8f8', 60, 140],
    ambient: [0xffffff, 0.45], hemi: [0xcfe8f8, 0x86b56a, 0.5], sun: [0xfff4e0, 1.4, [14, 22, 10]],
    build(g) {
      for (const [x, z, s] of [[-12, -8, 1.2], [10, -12, 1], [-8, -16, 1.4], [14, -6, 0.9], [-16, -4, 1]]) g.add(tree(x, z, s));
      addClouds(g, { count: 5, scale: 1, yMin: 12, yMax: 20 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  sunset: {
    sky: [[0, '#5b4a8a'], [0.45, '#d96a4f'], [0.75, '#f2a65a'], [1, '#f7d08a']],
    ground: 0x6f7a52, fog: ['#e8a06a', 60, 140],
    ambient: [0xffd9b0, 0.4], hemi: [0xf2a65a, 0x6f7a52, 0.45], sun: [0xff9a5c, 1.2, [-18, 8, 6]],
    build(g) {
      g.add(citySkyline(0x4a4a55, -18, 12, 8, 0xffb060, 0xe8a06a));
      const sun = new THREE.Mesh(new THREE.SphereGeometry(3, 12, 12), new THREE.MeshBasicMaterial({ color: 0xffc06a, fog: false }));
      sun.position.set(-30, 8, -60);
      g.add(sun);
      addClouds(g, { count: 4, scale: 1.1, tint: 0xf5c9a0, yMin: 14, yMax: 24, speed: 0.3 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  moscow_night: {
    night: true,
    sky: [[0, '#060a1e'], [0.6, '#101a3a'], [1, '#26304f']],
    ground: 0x2a3140, fog: ['#141c36', 70, 150],
    ambient: [0x8aa0ff, 0.35], hemi: [0x26304f, 0x2a3140, 0.35], sun: [0xbfd0ff, 0.6, [10, 25, 12]],
    build(g) {
      g.add(stars());
      const moon = new THREE.Mesh(new THREE.SphereGeometry(2.2, 12, 12), new THREE.MeshBasicMaterial({ color: 0xf5f1d0, fog: false }));
      moon.position.set(24, 34, -70);
      g.add(moon);
      g.add(citySkyline(0x1c2233, -20, 14, 10, 0xffd88a, 0x141c36));
      addClouds(g, { count: 3, scale: 1.3, tint: 0x3a4560, yMin: 20, yMax: 30, speed: 0.25 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  bangkok_neon: {
    night: true,
    sky: [[0, '#12041f'], [0.55, '#2a0f45'], [1, '#4a1d6e']],
    ground: 0x241a30, fog: ['#2a1440', 60, 140],
    ambient: [0xc080ff, 0.45], hemi: [0x4a1d6e, 0x241a30, 0.4], sun: [0xff70c0, 0.7, [12, 20, 10]],
    build(g) {
      g.add(citySkyline(0x1a1226, -16, 14, 12, 0xff3fa8, 0x2a1440));
      // Неоновые вывески: эмиссивные рамки
      const colors = [0xff2fa0, 0x2fd7ff, 0xb45cff, 0x37ff8b];
      for (let i = 0; i < 8; i++) {
        const strip = box(0.25, 3 + Math.random() * 6, 0.25, lambert(0x000000, colors[i % colors.length]),
          -34 + i * 9 + Math.random() * 3, 4 + Math.random() * 6, -14 - Math.random() * 6);
        g.add(strip);
      }
      addClouds(g, { count: 3, scale: 1.2, tint: 0x4a3060, yMin: 18, yMax: 28, speed: 0.3 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  moscow_fog: {
    sky: [[0, '#5c6a80'], [0.6, '#8b98ab'], [1, '#c2cbd8']],
    ground: 0x5d6672, fog: ['#9aa6b6', 25, 90],
    ambient: [0xdde6f0, 0.55], hemi: [0xc2cbd8, 0x5d6672, 0.55], sun: [0xe8f0ff, 0.8, [10, 25, 12]],
    build(g) {
      g.add(citySkyline(0x6e7c92, -16, 8, 16, 0xbcd7ff, 0x9aa6b6));
      addClouds(g, { count: 5, scale: 1.4, tint: 0xb8c2ce, yMin: 10, yMax: 22, speed: 0.5 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  spb_white: {
    sky: [[0, '#93b6d6'], [0.5, '#d8c8d8'], [0.8, '#f2d8c8'], [1, '#f8e8d8']],
    ground: 0x7d8a74, fog: ['#e8d8cc', 55, 130],
    ambient: [0xfff0e0, 0.55], hemi: [0xf2d8c8, 0x7d8a74, 0.5], sun: [0xffd8b0, 0.9, [-16, 10, 8]],
    build(g) {
      g.add(water(0x9db8c8, -18));
      g.add(citySkyline(0x8a8478, -14, 8, 5, 0xffe8b0, 0xe8d8cc));
      addClouds(g, { count: 4, scale: 1.1, tint: 0xf0d8cc, yMin: 16, yMax: 26, speed: 0.3 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  taipei_storm: {
    night: true,
    sky: [[0, '#1c2430'], [0.6, '#37424e'], [1, '#55606c']],
    ground: 0x3d4a44, fog: ['#414d59', 45, 120],
    ambient: [0xb8c8d8, 0.5], hemi: [0x55606c, 0x3d4a44, 0.45], sun: [0xd8e8f8, 0.7, [10, 25, 12]],
    build(g) {
      g.add(citySkyline(0x27303c, -16, 12, 9, 0x9fd8ff, 0x414d59));
      // Грозовой фронт: тёмные быстрые облака
      addClouds(g, { count: 6, scale: 1.5, tint: 0x46525c, yMin: 14, yMax: 28, speed: 1.1 });
    },
    // Молнии: случайные вспышки светом + дрейф туч
    update(dt, g, ctx) {
      driftClouds(g, dt);
      ctx.lightningTimer = (ctx.lightningTimer ?? 2 + Math.random() * 5) - dt;
      if (ctx.flash > 0) {
        ctx.flash -= dt;
        ctx.lights.ambient.intensity = 0.5 + ctx.flash * 8;
        if (ctx.flash <= 0) ctx.lights.ambient.intensity = 0.5;
      }
      if (ctx.lightningTimer <= 0) {
        ctx.lightningTimer = 2 + Math.random() * 5;
        ctx.flash = 0.18;
      }
    },
  },

  shanghai_clouds: {
    sky: [[0, '#3d7ec2'], [0.6, '#7db4e0'], [1, '#c8e2f2']],
    ground: 0x6f8a9a, fog: ['#a8cce4', 70, 160],
    ambient: [0xffffff, 0.55], hemi: [0xc8e2f2, 0x6f8a9a, 0.5], sun: [0xfff4e0, 1.3, [14, 30, 10]],
    build(g) {
      g.add(citySkyline(0x51677a, -18, 10, 7, 0xcfe8ff, 0xa8cce4));
      // Слой облаков на средней высоте — башня прорастает сквозь них
      addClouds(g, { count: 10, scale: 1.8, yMin: 26, yMax: 48, zMin: -18, zMax: -8, speed: 0.5 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  dubai_dawn: {
    sky: [[0, '#5a3a6e'], [0.4, '#c05a6e'], [0.7, '#f28a5a'], [1, '#f8c88a']],
    ground: 0xd8b878, fog: ['#e8a878', 60, 150],
    ambient: [0xffe0c0, 0.5], hemi: [0xf8c88a, 0xd8b878, 0.5], sun: [0xffa060, 1.1, [-20, 6, 8]],
    build(g) {
      // Дюны: сплющенные полусферы
      for (const [x, z, s] of [[-18, -14, 5], [14, -18, 7], [-6, -22, 6], [24, -10, 4]]) {
        const dune = new THREE.Mesh(new THREE.SphereGeometry(s, 10, 8), lambert(0xcfae6e));
        dune.scale.y = 0.22;
        dune.position.set(x, 0, z);
        g.add(dune);
      }
      const sun = new THREE.Mesh(new THREE.SphereGeometry(2.6, 12, 12), new THREE.MeshBasicMaterial({ color: 0xffb070, fog: false }));
      sun.position.set(-28, 6, -65);
      g.add(sun);
      addClouds(g, { count: 3, scale: 1.2, tint: 0xf5b890, yMin: 16, yMax: 26, speed: 0.25 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },

  phuket: {
    sky: [[0, '#2e9fd8'], [0.55, '#7fd0ef'], [1, '#d8f2fa']],
    ground: 0x8fc873, fog: ['#bfe8f5', 65, 150],
    ambient: [0xffffff, 0.55], hemi: [0xd8f2fa, 0x8fc873, 0.55], sun: [0xfff8e0, 1.5, [14, 26, 10]],
    build(g) {
      g.add(water(0x2e8fc8, -20));
      for (const [x, z] of [[-11, -7], [13, -9], [-15, -12], [9, -5], [17, -14]]) g.add(palm(x, z));
      // Биг Будда на дальнем холме
      const hill = new THREE.Mesh(new THREE.SphereGeometry(9, 10, 8), lambert(0x6aa85a));
      hill.scale.y = 0.5;
      hill.position.set(30, 0, -45);
      g.add(hill);
      const buddha = new THREE.Group();
      const body = new THREE.Mesh(new THREE.ConeGeometry(1.6, 3, 8), lambert(0xf5f5ea));
      body.position.y = 1.5;
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 8), lambert(0xf5f5ea));
      head.position.y = 3.4;
      buddha.add(body, head);
      buddha.position.set(30, 4.2, -45);
      g.add(buddha);
      addClouds(g, { count: 4, scale: 1.2, yMin: 14, yMax: 24, speed: 0.35 });
    },
    update(dt, g) { driftClouds(g, dt); },
  },
};

function driftClouds(group, dt) {
  for (const child of group.children) {
    if (child.userData.speed) {
      child.position.x += child.userData.speed * dt;
      if (child.position.x > 40) child.position.x = -40;
      if (child.position.x < -40) child.position.x = 40;
    }
  }
}

/**
 * Применяет тему уровня. Возвращает { update, dispose }.
 * lights — { ambient, sun } из createEngine.
 */
export function applyTheme(scene, lights, themeName) {
  const theme = THEMES[themeName] || THEMES.yard;
  const group = new THREE.Group();
  const ctx = { lights };
  let elapsed = 0;

  group.add(gradientSky(theme.sky));

  // Живность неба: днём стая птиц, ночью самолёт с маячком
  if (theme.night) group.add(nightPlane());
  else group.add(birdFlock(4 + Math.floor(Math.random() * 3), theme.birdColor || 0x3a3a3a));

  // Улица с машинками — город живёт у подножия стройки
  street(group, { z: -8, night: !!theme.night });

  // Земля
  const ground = new THREE.Mesh(new THREE.CircleGeometry(80, 28), lambert(theme.ground));
  ground.rotation.x = -Math.PI / 2;
  ground.receiveShadow = true;
  group.add(ground);

  // Бетонная плита фундамента — общая для всех уровней
  const slab = box(5, 0.5, 5, lambert(0xd8d4cc), 0, 0.25, 0);
  slab.receiveShadow = true;
  slab.castShadow = true;
  group.add(slab);

  // Туман и свет
  scene.fog = theme.fog ? new THREE.Fog(theme.fog[0], theme.fog[1], theme.fog[2]) : null;
  scene.background = new THREE.Color(theme.sky[theme.sky.length - 1][1]);
  lights.ambient.color.set(theme.ambient[0]);
  lights.ambient.intensity = theme.ambient[1];
  lights.sun.color.set(theme.sun[0]);
  lights.sun.intensity = theme.sun[1];
  lights.sun.position.set(...theme.sun[2]);
  // Полусферный свет: цвет неба сверху, отражение земли снизу
  if (lights.hemi) {
    const h = theme.hemi || [theme.sky[theme.sky.length - 1][1], theme.ground, 0.45];
    lights.hemi.color.set(h[0]);
    lights.hemi.groundColor.set(h[1]);
    lights.hemi.intensity = h[2];
  }

  if (theme.build) theme.build(group, ctx);
  scene.add(group);

  return {
    // camY — текущая высота камеры: по ней разводим планы параллаксом
    update(dt, camY = 0) {
      if (theme.update) theme.update(dt, group, ctx);
      elapsed += dt;
      updateCars(group, dt);
      for (const obj of group.children) {
        if (obj.userData.parallax) obj.position.y = (camY - 6) * obj.userData.parallax;
        if (obj.userData.flock) updateFlock(obj, dt, elapsed);
        if (obj.userData.plane) updatePlane(obj, dt, elapsed);
      }
      // Планы скайлайна лежат на слой глубже — им параллакс задан отдельно
      for (const obj of group.children) {
        if (!obj.userData.parallax) {
          for (const layer of obj.children) {
            if (layer.userData.parallax) layer.position.y = (camY - 6) * layer.userData.parallax;
          }
        }
      }
    },
    dispose() {
      scene.remove(group);
      group.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          for (const m of mats) {
            if (m.map) m.map.dispose();
            m.dispose(); // повторный dispose общих материалов безопасен
          }
        }
      });
    },
  };
}
